import { useEffect, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useAuth } from "@/auth";
import { ApiError } from "@/lib/api";
import { getOnboardingStatus } from "@/lib/profile-api";

/**
 * apps/web :: components :: HomeRedirectGate
 *
 * Wraps the `/` route so authed + onboarded visitors land on /dashboard
 * instead of the Phase-0 health-check page.
 *
 * Decision tree:
 *   - Auth state `loading`       → render children (Phase-0 UX unchanged).
 *   - Auth state `anonymous`     → render children (auth.spec.ts parity).
 *   - Auth state `authenticated` → probe onboarding-status:
 *       - `onboardingCompleted && hasProfile` → redirect to /dashboard.
 *       - otherwise                           → render children (the user
 *         will hit the full onboarding flow once they click through the
 *         app chrome; we don't force-redirect into /onboarding from /
 *         because that belongs to OnboardingGate's contract on the
 *         gated routes, not the public landing).
 *
 * Why keep HomePage as the children?
 *   - HomePage.tsx owns the Phase-0 backend-health card + marketing-ish
 *     hero. That's still the correct content for anonymous landings and
 *     for authed users mid-onboarding. Wrapping (not replacing) the page
 *     keeps the existing auth.spec.ts (anonymous sees login CTA) green
 *     AND preserves the existing HomePage tests without touching them.
 *
 * Error handling: if the onboarding-status probe throws we log at warn
 * level and fall through to rendering children. A flaky /api probe should
 * NEVER strand the user on a blank page.
 */
export default function HomeRedirectGate({ children }: { children: ReactNode }) {
  const { state: auth } = useAuth();
  const [shouldRedirect, setShouldRedirect] = useState<boolean | null>(null);

  useEffect(() => {
    if (auth.kind !== "authenticated") {
      // Nothing to probe; render children.
      setShouldRedirect(false);
      return;
    }
    let cancelled = false;
    setShouldRedirect(null);
    (async () => {
      try {
        const status = await getOnboardingStatus();
        if (cancelled) return;
        setShouldRedirect(status.hasProfile && status.onboardingCompleted);
      } catch (err) {
        if (cancelled) return;
        if (!(err instanceof ApiError)) {
          // eslint-disable-next-line no-console
          console.warn("HomeRedirectGate: onboarding-status probe failed", err);
        }
        setShouldRedirect(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.kind]);

  // Auth still booting — render children to avoid a blank flash. The Phase-0
  // page is safe to show while /auth/me is in flight; the probe below will
  // kick in once auth resolves.
  if (auth.kind === "loading") return <>{children}</>;
  // Anonymous visitors belong on the login/landing page.
  if (auth.kind === "anonymous") return <Navigate to="/login" replace />;

  // Authenticated: either we're still probing (render children so the
  // learner sees *something*), or the probe decided.
  if (shouldRedirect === true) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
